<template>
  <section class="my-account-page">
    <div class="account-container">
      <div class="my-account-page-header">
        <img alt="user icon illustration" src="../../assets/account-user-icon.svg" />
        <h1>My account</h1>
      </div>
      <div style="margin-bottom: 2rem" v-if="!successfulDetailChange">
        <EditableUserDetails
          :editableDetails="editableDetails"
          @toggle-success-message="toggleSuccessMessage"
        />
        <div v-if="!isChangingPassword" class="btn-container">
          <button class="btn btn-default" @click="toggleChangePassword" data-test="change-password">
            Change password
          </button>
        </div>
        <ChangePassword
          v-else
          @toggle-change-password="toggleChangePassword"
          @toggle-success-message="toggleSuccessMessage"
        />
      </div>
      <SuccessMessage
        v-else
        toggle-success-message="toggleSuccessMessage"
        :actionSuccess="successfulDetailChange"
        :detailChanged="detailsChanged"
        style="min-height: 320px"
      />
      <div v-if="csContext.user.subscriptionLevel === 'Full'">
        <div class="my-account-page-header">
          <img alt="membership icon illustration" src="../../assets/account-user-icon.svg" />
          <h1>My profile</h1>
        </div>
        <MyProfileSettings :userId="userId" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { csContext } from "../../services/cs-context";
import { MyAccountDetailsApiModel } from "../../api-models";
import EditableUserDetails from "../../components/my-account/editable-user-details.vue";
import ChangePassword from "../../components/my-account/change-password.vue";
import SuccessMessage from "../../components/my-account/success-message.vue";
import MyProfileSettings from "../../components/my-account/my-profile-settings.vue";

type SuccessfulDetailUpdate = {
  isDetailsUpdated: boolean;
  detailChanged: string;
  updatedDetails?: MyAccountDetailsApiModel;
};

const isChangingPassword = ref(false);

const successfulDetailChange = ref(false);

const detailsChanged = ref("details");

const updatedDetails = ref<MyAccountDetailsApiModel | null>(null);

const editableDetails = computed<MyAccountDetailsApiModel>(() => {
  const userDetails = {
    firstName: csContext.user.firstName,
    lastName: csContext.user.lastName,
    email: csContext.user.email,
  };
  return updatedDetails.value ?? userDetails;
});

const userId = computed<string>(() => {
  return csContext.user.id;
});

const toggleChangePassword = (): void => {
  isChangingPassword.value = !isChangingPassword.value;
};

const toggleSuccessMessage = (details: SuccessfulDetailUpdate): void => {
  successfulDetailChange.value = details.isDetailsUpdated;
  detailsChanged.value = details.detailChanged;
  if (details.updatedDetails) {
    updatedDetails.value = details.updatedDetails;
  }
  setTimeout(() => {
    successfulDetailChange.value = false;
  }, 3000);
};
</script>

<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/bootstrap.less";
@import (reference) "../../styles/bootstrap/variables.less";

@registration-text-color: @btn-disabled-color;

.my-account-page {
  width: 100%;
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.account-container {
  width: 50%;
  @media (max-width: 900px) {
    width: 60%;
  }
  @media (max-width: 450px) {
    width: 80%;
  }
}

.my-account-page-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2rem 0;
  margin-bottom: 2rem;

  h1 {
    margin: 0;
    @media (max-width: 450px) {
      font-size: 28px;
    }
  }

  img {
    margin-right: 2rem;
  }
}

.email-link {
  color: @brand-primary;
}

.btn-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.btn {
  margin-top: 2rem;
  @media (max-width: 450px) {
    margin-top: 1rem;
  }
}

.btn.btn-primary {
  vertical-align: center;
  padding-top: 8px;
}

.membership {
  height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  position: relative;
  @media (max-width: 450px) {
    width: 100%;
  }
}

.membership-card {
  text-align: center;
  box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  width: 75%;
  height: 50%;
  min-height: 15rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  @media (max-width: @screen-phone) {
    width: 100%;
    min-height: 150px;
  }

  @media (max-width: @screen-md) {
    padding: 2rem;
    min-width: 30rem;
  }
}

.membership-card-icon {
  width: 40%;
}

.membership-controls {
  display: flex;
  text-align: center;
  justify-content: center;

  a {
    margin: 2rem 4rem;
  }

  @media (max-width: @screen-phone) {
    text-align: center;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    a {
      margin: 0;
    }
  }
}

.cancellation-policy {
  text-align: center;

  @media (max-width: @screen-phone) {
    margin-top: 2rem;
  }
}
</style>
